import React from 'react';

import type { ExtensionManifest } from '@atlaskit/editor-common/extensions';
import {
	NATIVE_EMBED_EXTENSION_KEY,
	NATIVE_EMBED_EXTENSION_TYPE,
} from '@atlaskit/editor-common/extensions';
import type { PublicPluginAPI, CorePlugin } from '@atlaskit/editor-common/types';
import type { LocalIdPlugin } from '@atlaskit/editor-plugin-local-id';

import { NativeEmbedsFallbackExtension } from './ui';

const experienceIds = ['whiteboard', 'database', 'maui', 'page'];

type NativeEmbedsFallbackEditorAPI = PublicPluginAPI<[CorePlugin, LocalIdPlugin]>;

const convertNativeEmbedNodeToCard = ({
	api,
	localId,
	url,
}: {
	api?: NativeEmbedsFallbackEditorAPI;
	localId?: string;
	url?: string;
}) => {
	if (!localId) {
		return;
	}

	// If there is no URL, delete the node
	if (!url) {
		api?.core?.actions?.execute?.(({ tr }) => {
			const targetNode = api?.localId?.actions?.getNode({ localId });
			if (!targetNode) {
				return tr;
			}
			return tr.delete(targetNode.pos, targetNode.pos + targetNode.node.nodeSize);
		});
		return;
	}

	const schema = api?.core?.sharedState.currentState()?.schema;
	if (!schema) {
		return;
	}

	// Convert the native embed node to an inline card
	const inlineCard = schema.nodeFromJSON({
		type: 'paragraph',
		content: [{ type: 'inlineCard', attrs: { url } }],
	});
	api?.localId?.actions?.replaceNode({
		localId,
		value: inlineCard,
	});
};

export const createNativeEmbedsFallbackManifest = (
	api?: NativeEmbedsFallbackEditorAPI,
): ExtensionManifest => {
	return {
		title: 'Native Embed Fallback',
		type: NATIVE_EMBED_EXTENSION_TYPE,
		key: NATIVE_EMBED_EXTENSION_KEY,
		description: 'Converts native-embed extension into a smart card, when fallback is required.',
		modules: {
			nodes: {
				default: {
					type: 'extension',
					render: async () => (props) => (
						<NativeEmbedsFallbackExtension
							{...props}
							onConvert={() => {
								convertNativeEmbedNodeToCard({
									api,
									localId: props.node.localId,
									url: props.node.parameters?.url,
								});
							}}
						/>
					),
					// @ts-expect-error This option is experimental and not reflected in the manifest type definition
					__hideFrame: true,
				},
				...Object.fromEntries(
					experienceIds.map((experienceId) => [
						experienceId,
						{
							type: 'extension',
							render: async () => (props) => (
								<NativeEmbedsFallbackExtension
									{...props}
									onConvert={() => {
										convertNativeEmbedNodeToCard({
											api,
											localId: props.node.localId,
											url: props.node.parameters?.url,
										});
									}}
								/>
							),
							__hideFrame: true,
						},
					]),
				),
			},
		},
	};
};
